<template>
  <div class="material-panel">
    <aside class="material-panel__aside">
      <div class="group-head">
        <span class="group-head__title">分组</span>
        <el-button type="text" size="mini" icon="el-icon-plus" @click="openGroupDialog()">新建</el-button>
      </div>
      <ul class="group-list">
        <li
          :class="['group-item', currentGroupId === null ? 'is-active' : '']"
          @click="changeGroup(null)"
        >
          <span class="group-item__name">全部素材</span>
          <span class="group-item__count">{{ allCount }}</span>
        </li>
        <li
          v-for="group in groups"
          :key="group.id"
          :class="['group-item', currentGroupId === group.id ? 'is-active' : '']"
          @click="changeGroup(group.id)"
        >
          <span class="group-item__name" :title="group.name">{{ group.name }}</span>
          <span class="group-item__count">{{ group.count }}</span>
          <span v-if="group.id > 0" class="group-item__ops">
            <i class="el-icon-edit" title="重命名" @click.stop="openGroupDialog(group)" />
            <i class="el-icon-delete" title="删除分组" @click.stop="removeGroup(group)" />
          </span>
        </li>
      </ul>
    </aside>

    <section class="material-panel__main">
      <div class="toolbar">
        <el-upload
          class="toolbar__upload"
          action="#"
          accept="image/jpeg,image/png,image/gif,image/webp"
          :show-file-list="false"
          :http-request="handleUpload"
          :disabled="uploading"
        >
          <el-button size="small" type="primary" icon="el-icon-upload2" :loading="uploading">
            {{ uploading ? '上传中' : '上传到当前分组' }}
          </el-button>
        </el-upload>
        <el-input
          v-model="keyword"
          size="small"
          clearable
          class="toolbar__search"
          placeholder="搜索素材名称"
          prefix-icon="el-icon-search"
          @keyup.enter.native="reload"
          @clear="reload"
        />
        <div class="toolbar__spacer" />
        <span class="toolbar__selected">已选 {{ selectedIds.length }}</span>
        <el-button size="small" :disabled="!selectedIds.length" @click="openMoveDialog">移动到</el-button>
        <el-button size="small" type="danger" plain :disabled="!selectedIds.length" @click="batchDelete">删除</el-button>
        <el-button size="small" icon="el-icon-refresh" circle @click="reload" />
      </div>

      <div v-loading="loading" class="grid">
        <div v-if="!list.length && !loading" class="grid__empty">
          暂无素材，点击上方「上传到当前分组」添加
        </div>
        <div
          v-for="item in list"
          :key="item.id"
          :class="['card', isChecked(item) ? 'is-checked' : '']"
          @click="toggle(item)"
        >
          <div class="card__thumb">
            <img v-if="item.type === 1" :src="item.url" :alt="item.name" />
            <i v-else class="el-icon-document card__file" />
            <span class="card__check"><i class="el-icon-check" /></span>
            <span class="card__preview" @click.stop="preview(item)">
              <i class="el-icon-zoom-in" />
            </span>
          </div>
          <div class="card__name" :title="item.name">{{ item.name }}</div>
          <div class="card__meta">{{ item.size_text }}</div>
        </div>
      </div>

      <div class="pager">
        <el-pagination
          background
          layout="total, prev, pager, next"
          :current-page="page"
          :page-size="limit"
          :total="count"
          @current-change="onPageChange"
        />
      </div>
    </section>

    <el-dialog
      title="预览"
      :visible="previewVisible"
      width="720px"
      append-to-body
      @close="previewVisible = false"
    >
      <img v-if="previewUrl" :src="previewUrl" class="preview-img" alt="" />
    </el-dialog>

    <el-dialog
      title="移动到分组"
      :visible="moveVisible"
      width="360px"
      append-to-body
      @close="moveVisible = false"
    >
      <el-select v-model="moveTarget" placeholder="请选择分组" style="width: 100%">
        <el-option
          v-for="group in groups"
          :key="group.id"
          :label="group.name"
          :value="group.id"
        />
      </el-select>
      <div slot="footer">
        <el-button size="small" @click="moveVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="moving" @click="submitMove">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getGroupList,
  saveGroup,
  deleteGroup,
  getAttachmentList,
  uploadAttachment,
  moveAttachments,
  deleteAttachments
} from '@/api/attachment'

export default {
  name: 'MaterialPanel',
  props: {
    // manage：纯管理；pick：带选中确认
    mode: {
      type: String,
      default: 'manage'
    },
    multiple: {
      type: Boolean,
      default: true
    },
    // 已选中的素材地址，用于回显勾选状态
    selectedUrls: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      groups: [],
      currentGroupId: null,
      list: [],
      count: 0,
      page: 1,
      limit: 24,
      keyword: '',
      loading: false,
      uploading: false,
      selectedMap: {},
      previewVisible: false,
      previewUrl: '',
      moveVisible: false,
      moveTarget: 0,
      moving: false
    }
  },
  computed: {
    allCount() {
      return this.groups.reduce((total, group) => total + (group.count || 0), 0)
    },
    selectedIds() {
      return Object.keys(this.selectedMap).map((id) => Number(id))
    }
  },
  mounted() {
    this.loadGroups()
    this.reload()
  },
  methods: {
    loadGroups() {
      return getGroupList()
        .then((data) => {
          this.groups = Array.isArray(data) ? data : []
        })
        .catch(() => {})
    },
    reload() {
      this.page = 1
      this.loadList()
    },
    loadList() {
      this.loading = true
      const params = { page: this.page, limit: this.limit }
      if (this.currentGroupId !== null) {
        params.group_id = this.currentGroupId
      }
      if (this.keyword) {
        params.keyword = this.keyword
      }

      return getAttachmentList(params)
        .then((data) => {
          const result = data || {}
          this.list = result.list || []
          this.count = result.count || 0
          this.syncSelected()
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false
        })
    },
    // 把已选地址映射到当前列表的勾选状态
    syncSelected() {
      if (!this.selectedUrls.length) return
      this.list.forEach((item) => {
        if (this.selectedUrls.indexOf(item.url) > -1 && !this.selectedMap[item.id]) {
          this.$set(this.selectedMap, item.id, item)
        }
      })
    },
    changeGroup(groupId) {
      if (this.currentGroupId === groupId) return
      this.currentGroupId = groupId
      this.reload()
    },
    isChecked(item) {
      return !!this.selectedMap[item.id]
    },
    toggle(item) {
      if (this.multiple) {
        if (this.selectedMap[item.id]) {
          this.$delete(this.selectedMap, item.id)
        } else {
          this.$set(this.selectedMap, item.id, item)
        }
        return
      }
      const only = this.selectedMap[item.id] && Object.keys(this.selectedMap).length === 1
      this.selectedMap = {}
      if (!only) {
        this.$set(this.selectedMap, item.id, item)
      }
    },
    selectedAssets() {
      return this.selectedIds.map((id) => this.selectedMap[id]).filter(Boolean)
    },
    notifyChange() {
      this.$emit('change', this.selectedAssets())
    },
    preview(item) {
      if (item.type !== 1) return
      this.previewUrl = item.url
      this.previewVisible = true
    },
    handleUpload(options) {
      const file = options.file
      if (!file) return
      this.uploading = true
      const groupId = this.currentGroupId === null ? 0 : this.currentGroupId
      uploadAttachment({ file, groupId, type: 1 })
        .then(() => {
          this.$message.success('上传成功')
          this.loadGroups()
          this.reload()
        })
        .catch(() => {})
        .finally(() => {
          this.uploading = false
        })
    },
    openGroupDialog(group) {
      const isEdit = !!group
      this.$prompt('请输入分组名称', isEdit ? '编辑分组' : '新建分组', {
        inputValue: isEdit ? group.name : '',
        inputValidator: (value) => (value && value.trim() ? true : '请输入分组名称')
      })
        .then(({ value }) => {
          const payload = { name: value.trim() }
          if (isEdit) payload.id = group.id
          return saveGroup(payload).then(() => {
            this.$message.success(isEdit ? '分组已更新' : '分组已创建')
            return this.loadGroups()
          })
        })
        .catch(() => {})
    },
    removeGroup(group) {
      this.$confirm(
        `删除分组「${group.name}」后，组内 ${group.count} 个素材会移到「未分组」，确定删除吗？`,
        '提示',
        { type: 'warning' }
      )
        .then(() => deleteGroup(group.id))
        .then(() => {
          this.$message.success('分组已删除')
          if (this.currentGroupId === group.id) {
            this.currentGroupId = null
          }
          this.loadGroups()
          this.reload()
        })
        .catch(() => {})
    },
    openMoveDialog() {
      if (!this.selectedIds.length) return
      this.moveTarget = this.currentGroupId === null ? 0 : this.currentGroupId
      this.moveVisible = true
    },
    submitMove() {
      if (!this.selectedIds.length) return
      this.moving = true
      moveAttachments(this.selectedIds, this.moveTarget)
        .then((data) => {
          const affected = data && data.affected ? data.affected : 0
          this.$message.success(`已移动 ${affected} 个素材`)
          this.selectedMap = {}
          this.moveVisible = false
          this.loadGroups()
          this.loadList()
        })
        .catch(() => {})
        .finally(() => {
          this.moving = false
        })
    },
    batchDelete() {
      if (!this.selectedIds.length) return
      this.$confirm(
        `确定删除选中的 ${this.selectedIds.length} 个素材吗？文件会同时从磁盘删除。`,
        '提示',
        { type: 'warning' }
      )
        .then(() => deleteAttachments(this.selectedIds))
        .then((data) => {
          const affected = data && data.affected ? data.affected : 0
          this.$message.success(`已删除 ${affected} 个素材`)
          this.selectedMap = {}
          this.loadGroups()
          this.loadList()
        })
        .catch(() => {})
    },
    onPageChange(page) {
      this.page = page
      this.loadList()
    },
    // 供父组件（弹窗）调用
    getSelection() {
      return this.selectedAssets()
    },
    clearSelection() {
      this.selectedMap = {}
    }
  }
}
</script>

<style lang="scss" scoped>
.material-panel {
  display: flex;
  min-height: 420px;
  border: 1px solid var(--app-border);
  border-radius: 12px;
  overflow: hidden;
  background: var(--app-bg-card);
}

.material-panel__aside {
  width: 196px;
  flex-shrink: 0;
  border-right: 1px solid var(--app-border);
  background: #f8fbf9;
  display: flex;
  flex-direction: column;
}

.group-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid var(--app-border);

  &__title {
    font-size: 13px;
    font-weight: 600;
    color: var(--app-text);
  }
}

.group-list {
  list-style: none;
  margin: 0;
  padding: 6px;
  overflow: auto;
  flex: 1;
}

.group-item {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 10px;
  border-radius: 8px;
  cursor: pointer;
  color: var(--app-text-secondary);
  font-size: 13px;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: rgba(79, 138, 118, 0.08);
  }

  &.is-active {
    background: rgba(79, 138, 118, 0.14);
    color: var(--app-primary);
    font-weight: 600;
  }

  &__name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__count {
    font-size: 12px;
    opacity: 0.75;
  }

  &__ops {
    display: none;
    gap: 6px;

    i {
      font-size: 13px;
      color: var(--app-text-secondary);

      &:hover {
        color: var(--app-primary);
      }
    }
  }

  &:hover &__ops {
    display: inline-flex;
  }
}

.material-panel__main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding: 12px 14px 8px;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 12px;

  &__upload {
    display: inline-block;
  }

  &__search {
    width: 200px;
  }

  &__spacer {
    flex: 1;
  }

  &__selected {
    font-size: 12px;
    color: var(--app-text-secondary);
  }
}

.grid {
  flex: 1;
  min-height: 260px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-content: flex-start;
}

.grid__empty {
  width: 100%;
  padding: 60px 0;
  text-align: center;
  color: var(--app-text-secondary);
  font-size: 13px;
}

.card {
  width: 104px;
  border: 1px solid var(--app-border);
  border-radius: 10px;
  padding: 6px;
  cursor: pointer;
  background: #fff;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:hover {
    border-color: var(--app-primary-muted);
    box-shadow: 0 4px 12px rgba(79, 138, 118, 0.12);
  }

  &.is-checked {
    border-color: var(--app-primary);
    box-shadow: 0 0 0 1px var(--app-primary) inset;
  }

  &__thumb {
    position: relative;
    width: 100%;
    height: 90px;
    border-radius: 6px;
    overflow: hidden;
    background: #f2f6f4;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  }

  &__file {
    font-size: 30px;
    color: #9bb3aa;
  }

  &__check {
    position: absolute;
    right: 4px;
    bottom: 4px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: var(--app-primary);
    color: #fff;
    font-size: 12px;
    display: none;
    align-items: center;
    justify-content: center;
  }

  &.is-checked &__check {
    display: flex;
  }

  &__preview {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.45);
    color: #fff;
    font-size: 12px;
    display: none;
    align-items: center;
    justify-content: center;
  }

  &:hover &__preview {
    display: flex;
  }

  &__name {
    margin-top: 6px;
    font-size: 12px;
    color: var(--app-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__meta {
    font-size: 11px;
    color: var(--app-text-secondary);
    margin-top: 2px;
  }
}

.pager {
  display: flex;
  justify-content: flex-end;
  padding-top: 10px;
}

.preview-img {
  display: block;
  max-width: 100%;
  max-height: 70vh;
  margin: 0 auto;
}
</style>
